import { Menu, Layout } from "antd";
import styled from "styled-components";

import { Logo } from "./Logo";
import { AddUserIcon, ClinicIcon, HomeIcon, ProfileIcon, TermsIcon } from "./icons";
import { useTranslation } from "react-i18next";
import { LogoutButton } from "./Logout";
import {
  PATH_ADMINS,
  PATH_CLIENT_INFO,
  PATH_CLINIC,
  PATH_GENERATE_COUPON,
  PATH_COUPON_LIST,
  PATH_HOME,
  PATH_COUPON_REDEEM,
  enumSubdomain,
  PATH_REDEEM_COUPON,
  PATH_PATIENT,
  PATH_TERMS_AND_CONDITIONS,
  PATH_PRIVACY_POLICY,
  PATH_MY_PROFILE,
  enumAdminRole,
  enumJoinedProgram,
  PATH_SRHR_PATIENT,
  PATH_HEALTH_RECORDS,
  PATH_DEPO_RECORDS,
  PATH_SALE_ORDER,
  PATH_PRODUCT_MASTER,
  PATH_INVENTORY_MASTER,
  PATH_LOGISTIC,
  PATH_REPORTS,
  PATH_CDIC_COUPON_REDEEM,
  PATH_CDIC_CLIENT_INFO,
  PATH_CDIC_COUPON_LIST,
  PATH_GENERATE_CDIC_COUPON,
  PATH_REDEEM_CDIC_COUPON,
  PATH_CDIC_PATIENT,
} from "@configs";
import { useHistory, useLocation } from "react-router";
import { device } from "@utils";
import { useDispatch, useSelector } from "react-redux";
import { selectApp, selectAuth, setDrawer } from "@redux";
import { FileProtectOutlined } from "@ant-design/icons";
import BabyDarkIcon from "@components/icons/svg/baby_dark_icon.svg";
import CalendarDarkIcon from "@components/icons/svg/calendar_dark_icon.svg";
const { Sider } = Layout;

interface IMenu {
  key: string;
  icon?: React.ReactNode;
  label: string;
  children?: IMenu[];
}

export const ComponentSidebar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const { drawer } = useSelector(selectApp);
  const { auth } = useSelector(selectAuth);
  const dispatch = useDispatch();
  const { mobile, domain, program } = useSelector(selectApp);
  const isClinicApp = () => domain === enumSubdomain.CLINIC || domain === enumSubdomain.STG_CLINIC;
  const srhrData = [
    {
      key: "SRHR",
      icon: (
        <IconCover>
          <img src={CalendarDarkIcon} alt="icon" width={22} height={22} />
        </IconCover>
      ),
      label: t("page.sidebar.SRHR_PROGRAM"),
      children: [
        {
          key: PATH_DEPO_RECORDS,
          label: t("page.sidebar.depo_records"),
        },
        {
          key: PATH_HEALTH_RECORDS,
          label: t("page.sidebar.health_records"),
        },
        {
          key: PATH_SRHR_PATIENT,
          label: t("page.sidebar.srhr_patient_list"),
        },
      ],
    },
  ];
  const ePharmacy = [
    {
      key: "PHARMACY",
      icon: (
        <IconCover>
          <img src={CalendarDarkIcon} alt="icon" width={22} height={22} />
        </IconCover>
      ),
      label: t("page.sidebar.epharmacy"),
      children: [
        {
          key: PATH_SALE_ORDER,
          label: t("page.sidebar.sale_order_list"),
        },
        {
          key: PATH_PRODUCT_MASTER,
          label: t("page.sidebar.product_master"),
        },
        {
          key: PATH_INVENTORY_MASTER,
          label: t("page.sidebar.inventory_master"),
        },
        {
          key: PATH_LOGISTIC,
          label: t("page.sidebar.logistic"),
        },
        {
          key: PATH_REPORTS,
          label: t("page.sidebar.reports"),
        },
      ],
    },
  ];
  const mnchData = [
    {
      key: "MNCH",
      icon: (
        <IconCover>
          <img src={BabyDarkIcon} alt="icon" width={22} height={22} />
        </IconCover>
      ),
      label: t("page.sidebar.MNCH_PROGRAM"),
      children: [
        {
          key: PATH_COUPON_REDEEM,
          label: t("page.sidebar.redeem_coupon_information"),
        },
        {
          key: PATH_CLIENT_INFO,
          label: t("page.sidebar.client_information"),
        },
        {
          key: PATH_COUPON_LIST,
          label: t("page.sidebar.coupon_management"),
        },
        {
          key: PATH_GENERATE_COUPON,
          label: t("page.sidebar.coupon_create"),
        },
      ],
    },
  ];
  const cdicData = [
    {
      key: "CDIC",
      icon: (
        <IconCover>
          <img src={BabyDarkIcon} alt="icon" width={22} height={22} />
        </IconCover>
      ),
      label: t("page.sidebar.CDIC_PROGRAM"),
      children: [
        {
          key: PATH_CDIC_COUPON_REDEEM,
          label: t("page.sidebar.redeem_coupon_information"),
        },
        {
          key: PATH_CDIC_CLIENT_INFO,
          label: t("page.sidebar.client_information"),
        },
        {
          key: PATH_CDIC_COUPON_LIST,
          label: t("page.sidebar.coupon_management"),
        },
        {
          key: PATH_GENERATE_CDIC_COUPON,
          label: t("page.sidebar.coupon_create"),
        },
      ],
    },
  ];
  const cdicClinic = [
    {
      key: "CDIC",
      icon: (
        <IconCover>
          <img src={BabyDarkIcon} alt="icon" width={22} height={22} />
        </IconCover>
      ),
      label: t("page.sidebar.CDIC_PROGRAM"),
      children: [
        {
          key: PATH_REDEEM_CDIC_COUPON,
          label: t("page.sidebar.redeem_cdic_coupon"),
        },
        {
          key: PATH_CDIC_PATIENT,
          label: t("page.sidebar.patient_list"),
        },
      ],
    },
  ];
  const mnchDataClinic = [
    {
      key: "MNCH",
      icon: (
        <IconCover>
          <img src={BabyDarkIcon} alt="icon" width={22} height={22} />
        </IconCover>
      ),
      label: t("page.sidebar.MNCH_PROGRAM"),
      children: [
        {
          key: PATH_REDEEM_COUPON,
          label: t("page.sidebar.redeem_coupon"),
        },
        {
          key: PATH_PATIENT,
          label: t("page.sidebar.patient_list"),
        },
      ],
    },
  ];
  const SRHRProgram =
    program && program.length > 0 && program.includes(enumJoinedProgram.SRHR) ? srhrData : [];
  const MNCHProgram =
    program && program.length > 0 && program.includes(enumJoinedProgram.MNCH) ? [...cdicData, ...mnchData] : [];
  const clinicMNCHProgram =
    program && program.length > 0 && program.includes(enumJoinedProgram.MNCH) ? [...mnchDataClinic] : [];
  const cdicProgram =
    program && program.length > 0 && program.includes(enumJoinedProgram.CDIC) ? [...cdicClinic] : [];
  const clinicRoute = [
    {
      key: PATH_TERMS_AND_CONDITIONS,
      icon: (
        <IconCover>
          <TermsIcon />
        </IconCover>
      ),
      label: t("page.sidebar.terms_and_conditions"),
    },
    {
      key: PATH_PRIVACY_POLICY,
      icon: (
        <IconCover>
          <FileProtectOutlined style={{ fontSize: "22px" }} />
        </IconCover>
      ),
      label: t("page.sidebar.privacy_policy"),
    },
    {
      key: PATH_MY_PROFILE,
      icon: (
        <IconCover>
          <ProfileIcon />
        </IconCover>
      ),
      label: t("page.sidebar.my_profile"),
    },
  ];
  const adminRoutes = [
    {
      key: PATH_HOME,
      icon: (
        <IconCover>
          <HomeIcon />
        </IconCover>
      ),
      label: t("page.sidebar.home"),
    },
  ];
  const adminMenu =
    auth?.userRole === enumAdminRole.SUPER
      ? [
          {
            key: PATH_CLINIC,
            icon: (
              <IconCover>
                <ClinicIcon />
              </IconCover>
            ),
            label: t("page.sidebar.clinic"),
          },
          {
            key: PATH_ADMINS,
            icon: (
              <IconCover>
                <AddUserIcon />
              </IconCover>
            ),
            label: t("page.sidebar.admins"),
          },
        ]
      : [];
  const menu: IMenu[] = isClinicApp()
    ? [...cdicProgram, ...clinicMNCHProgram, ...SRHRProgram, ...clinicRoute]
    : [...adminRoutes, ...MNCHProgram, ...srhrData, ...ePharmacy, ...adminMenu];
  const changeMenu = async ({ key }: any) => {
    if (mobile) {
      await dispatch(setDrawer(!drawer));
    }
    await history.push(key);
  };
  return (
    <StyledSidebar width={"100%"} theme="light">
      <div className="layout">
        <div>
          <Logo />
          <StyledMenu
            theme="light"
            mode="inline"
            onClick={changeMenu}
            defaultOpenKeys={["CDIC", "MNCH", "SRHR", "PHARMACY", PATH_COUPON_LIST]}
            selectedKeys={[pathname]}
            items={menu}
          />
        </div>
        <LogoutButton />
      </div>
    </StyledSidebar>
  );
};
const IconCover = styled.div`
  width: 43px;
  height: 43px;
  background: #ffffff;
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  margin: auto;
  padding: 9px;
`;
const StyledSidebar = styled(Sider)`
  padding: 1.25rem 1.313rem 1.25rem 1.5rem;
  min-width: 300px !important;
  box-shadow: rgba(14, 30, 37, 0.12) 0px 2px 4px 0px, rgba(14, 30, 37, 0.32) 0px 2px 16px 0px;
  max-height: 100vh;
  overflow-y: scroll;
  .layout {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }
  @media ${device.tablet} {
    padding: 25px 30px;
    width: auto !important;
    min-width: 280px !important;
    height: 100vh;
    .ant-layout-sider-children {
      height: 100%;
    }
  }
`;
const StyledMenu = styled(Menu)`
  border: 0 !important;
  margin-top: 30px;
  /* children menu */
  .ant-menu-submenu {
    .ant-menu-submenu-title {
      margin: 20px 8px;
      height: 55px;
      border-radius: 8px;
      font-weight: 500;
      font-size: 16px;
      color: #8090a9;
      padding-left: 18px !important;
      &:hover {
        color: #f06d24 !important;
      }
    }
    .ant-menu-sub {
      margin-left: 30px;
      background-color: #fff;
      .ant-menu-item {
        height: 55px;
        border-radius: 8px;
        font-weight: 500;
        font-size: 16px;
        color: #8090a9;
        margin-top: 0;
        margin-bottom: 4px;
      }
    }
  }
  /* parent menu */
  .ant-menu-item {
    height: 55px;
    border-radius: 8px;
    font-weight: 500;
    font-size: 16px;
    color: #8090a9;
    padding-left: 18px !important;
    margin: 20px 8px;
    &.ant-menu-item-selected,
    &:hover {
      color: #f06d24 !important;
      .ant-menu-item-icon {
        background-color: #f06d24;
        box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.1);
        svg {
          rect {
            fill: transparent;
          }
          path {
            fill: #fff;
          }
        }
      }
    }
  }
  @media ${device.tablet} {
    .ant-menu-submenu {
      .ant-menu-submenu-title {
        margin-left: 0;
      }
    }
    .ant-menu-item {
      margin-left: 0;
    }
  }
`;
